import { ChangeDetectorRef, DestroyRef, Directive, effect, ElementRef, inject, input, output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime, Observable, Subscription } from 'rxjs'
import { isServer } from '../server-utils'

export type IntersectEvent = { intersect: boolean; ratio: number }
export type IntersectConfig = {
	root?: ElementRef | null
	margin?: string
	threshold?: number | number[]
	debounce?: number
}

/**
 * Use the Intersection Observer API to act on elements entering or exiting or partially entering/exiting the viewport or specified element.
 *
 * Note: The margin property accepts values in pixels and percentages, in longhand or shorthand form. It doesn't seem to always work as expected.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 *
 * Usage (with elementRefDirective):
 * ```html
 * <div class="scrollable" #rootRef="elementRef">
 *     <div [mosIntersection]="{root: rootRef} (intersect)="onIntersect($event)"></div>
 * </div>
 * ```
 */
@Directive({
	selector: '[mosIntersection]',
	exportAs: 'intersection',
})
export class IntersectionDirective {
	private element = inject(ElementRef)
	private changeDetectorRef = inject(ChangeDetectorRef)
	private root?: ElementRef | null = null
	private margin = '0px'
	private threshold: number | number[] =
		/* every number between 0 and 1, inclusive, 0.01 step */
		[...Array(101).keys()].map((i) => i / 100)
	private debounce = 0
	private subscription?: Subscription

	destroyRef = inject(DestroyRef)

	readonly mosIntersection = input.required<IntersectConfig>()

	constructor() {
		effect(() => {
			const value = this.mosIntersection()

			this.root = value.root?.nativeElement ?? this.root
			this.margin = value.margin ?? this.margin
			this.threshold = value.threshold ?? this.threshold
			this.debounce = value.debounce ?? this.debounce

			this.createObserver()
		})
	}

	readonly intersect = output<IntersectEvent>()
	isIntersecting = false
	ratio = 0

	createObserver() {
		const options: IntersectionObserverInit = {
			root: this.root?.nativeElement || null,
			rootMargin: this.margin,
			threshold: this.threshold,
		}

		// Unsubscribe from previous observable if any
		this.subscription?.unsubscribe()

		this.subscription = new Observable<IntersectionObserverEntry>((subscriber) => {
			if (isServer)
				return {
					unsubscribe() {
						// Polyfill for SSR
					},
				}

			const intersectionObserver = new IntersectionObserver((entries) => {
				subscriber.next(entries[0])
			}, options)

			intersectionObserver.observe(this.element.nativeElement)

			return {
				unsubscribe() {
					intersectionObserver.disconnect()
				},
			}
		})
			.pipe(debounceTime(this.debounce), takeUntilDestroyed(this.destroyRef))
			.subscribe((entry) => {
				this.isIntersecting = entry.isIntersecting
				this.ratio = entry.intersectionRatio
				this.intersect.emit({ intersect: this.isIntersecting, ratio: this.ratio })
				this.changeDetectorRef.markForCheck()
			})
	}
}
